import { StrategyBuilderFunction, EMPTY_STRATEGY } from './types';
import { addLongPut, addShortPut } from './addOption';
import {
  getDefaultExpiration,
  findClosestStrikePrice,
  getStrikesAtExpiration,
  expirationToDate,
  getPremium,
  getStrategyLegStrikeDirection,
  getStrikeDistance,
} from './utils';

export const longPutCondor: StrategyBuilderFunction = ({
  chain,
  spot,
  expirationDate,
  expirationKey,
  strikeDirection,
  contractMultiplier = 1,
}) => {
  const strikesAtExpiration = expirationKey
    ? getStrikesAtExpiration(chain, expirationKey)
    : getDefaultExpiration(chain);

  if (
    !strikesAtExpiration ||
    !strikesAtExpiration.e ||
    !strikesAtExpiration.ey ||
    !strikesAtExpiration.em ||
    !strikesAtExpiration.ed
  ) {
    return EMPTY_STRATEGY;
  }

  const expirationDateForOptions =
    expirationDate ||
    expirationToDate(strikesAtExpiration.ey, strikesAtExpiration.em, strikesAtExpiration.ed);
  const expirationKeyForOptions = expirationKey || strikesAtExpiration.e;

  // Define strike prices
  const lowestStrike = findClosestStrikePrice(
    strikesAtExpiration,
    getStrikeDistance({ price: spot, unit: -2 }),
  ); // Buying 1 OTM put
  const lowerMiddleStrike = findClosestStrikePrice(
    strikesAtExpiration,
    getStrikeDistance({ price: spot, unit: -1 }),
  ); // Selling 1 ITM or ATM put
  const upperMiddleStrike = findClosestStrikePrice(
    strikesAtExpiration,
    getStrikeDistance({ price: spot, unit: 1 }),
  ); // Selling 1 OTM put
  const highestStrike = findClosestStrikePrice(
    strikesAtExpiration,
    getStrikeDistance({ price: spot, unit: 2 }),
  ); // Buying 1 OTM put

  return {
    optionPositions: [
      // Buy 1 OTM put option at the lowest strike
      addLongPut({
        strike: lowestStrike,
        premium: getPremium({
          ask: strikesAtExpiration?.c?.[lowestStrike]?.p?.a || 0,
          bid: strikesAtExpiration?.c?.[lowestStrike]?.p?.b || 0,
          strikeDirection: getStrategyLegStrikeDirection(strikeDirection, 'long'),
        }),
        expirationDate: expirationDateForOptions,
        expirationKey: expirationKeyForOptions,
        numberOfContracts: 1 * contractMultiplier,
      }),
      // Sell 1 ITM or ATM put option at the lower middle strike
      addShortPut({
        strike: lowerMiddleStrike,
        premium: getPremium({
          ask: strikesAtExpiration?.c?.[lowerMiddleStrike]?.p?.a || 0,
          bid: strikesAtExpiration?.c?.[lowerMiddleStrike]?.p?.b || 0,
          strikeDirection: getStrategyLegStrikeDirection(strikeDirection, 'short'),
        }),
        expirationDate: expirationDateForOptions,
        expirationKey: expirationKeyForOptions,
        numberOfContracts: 1 * contractMultiplier,
      }),
      // Sell 1 OTM put option at the upper middle strike
      addShortPut({
        strike: upperMiddleStrike,
        premium: getPremium({
          ask: strikesAtExpiration?.c?.[upperMiddleStrike]?.p?.a || 0,
          bid: strikesAtExpiration?.c?.[upperMiddleStrike]?.p?.b || 0,
          strikeDirection: getStrategyLegStrikeDirection(strikeDirection, 'short'),
        }),
        expirationDate: expirationDateForOptions,
        expirationKey: expirationKeyForOptions,
        numberOfContracts: 1 * contractMultiplier,
      }),
      // Buy 1 OTM put option at the highest strike
      addLongPut({
        strike: highestStrike,
        premium: getPremium({
          ask: strikesAtExpiration?.c?.[highestStrike]?.p?.a || 0,
          bid: strikesAtExpiration?.c?.[highestStrike]?.p?.b || 0,
          strikeDirection: getStrategyLegStrikeDirection(strikeDirection, 'long'),
        }),
        expirationDate: expirationDateForOptions,
        expirationKey: expirationKeyForOptions,
        numberOfContracts: 1 * contractMultiplier,
      }),
    ],
    stockPositions: [],
  };
};
